/** Shapes the cookie governance screens read, and the small formatters they share. */

export interface CookieRow {
  id: string;
  name: string;
  host: string;
  vendor: string;
  party: 'first' | 'third';
  proposedPurposeId: string | null;
  purposeId: string | null;
  status: 'proposed' | 'confirmed' | 'ignored';
  source: 'scan' | 'observed' | 'manual';
  description: string;
  retention: string;
  occurrences: number;
  foundOn: string[];
  firstSeen: string;
  lastSeen: string;
  reviewedBy: string | null;
  expiryDays: number | null;
  expirySource: 'unknown' | 'session' | 'set_cookie' | 'catalogue' | 'manual';
  owner: string;
  processorId: string | null;
  /** Browser crawl: present before consent in the latest crawl that found it. */
  setBeforeConsent?: boolean;
  lastSeenBeforeConsent?: string | null;
  httpOnly?: boolean | null;
  secure?: boolean | null;
  sameSite?: string | null;
}

export interface PageResult {
  url: string;
  ok: boolean;
  error?: string;
  vendors: string[];
  tagContainer: boolean;
  /** Browser crawl pages only. */
  mode?: 'browser';
  cookiesBefore?: number;
  cookiesAfter?: number;
  beforeConsent?: string[];
  vendorsBeforeConsent?: string[];
  hosts?: string[];
  unattributedHosts?: string[];
  storageKeys?: string[];
  acceptedVia?: string | null;
}

export type ScanMode = 'html' | 'browser';

export const MODE_LABEL: Record<ScanMode, string> = { html: 'Page HTML', browser: 'Browser crawl' };

export interface ScannerStatus {
  status: 'running' | 'stopped' | 'not_deployed';
  lastSeenAt: string | null;
  crawlers: number;
  busy: number;
  maxPages: number | null;
  concurrency: number | null;
  browserVersion: string | null;
  queued: number;
}

export interface ScanRow {
  id: string;
  urls: string[];
  mode?: ScanMode;
  status: 'queued' | 'running' | 'complete' | 'error' | string;
  pagesFetched: number;
  pagesFailed: number;
  findings: number;
  newFindings: number;
  pageResults?: PageResult[];
  startedAt: string;
  finishedAt: string | null;
  error: string | null;
  /** Not yet returned by every API build; shown when present. */
  requestedBy?: string | null;
  scheduleId?: string | null;
}

export interface ScheduleRow {
  id: string;
  name: string;
  collectionPointId: string | null;
  urls: string[];
  mode?: ScanMode;
  everyDays: number;
  notify: string[];
  status: 'active' | 'paused';
  nextRunAt: string;
  lastRunAt: string | null;
  lastScanId: string | null;
}

export interface AlertRow {
  id: string;
  scanId: string;
  cookies: Array<{ id: string; name: string; vendor: string; status: string }>;
  status: 'open' | 'closed';
  notified: string[];
  createdAt: string;
  closedAt: string | null;
}

export interface PurposeRow {
  id: string;
  key: string;
  status: string;
  current: { name: string; basis: 'consent' | 'legitimate_use' };
}

export interface PointRow { id: string; name: string; channel: string; apiKeyPrefix?: string; status?: string; geoRules?: GeoRule[]; consentMaxAgeDays?: number | null }

export interface ProcessorRow { id: string; name: string; status: string }

export interface UserRow { email: string; name: string; status: string }

export interface CookieDashboard {
  days: number;
  totals: {
    cookies: number; confirmed: number; waiting: number; notDeclared: number; thirdParty: number; vendors: number;
    /** Not returned by API builds before the browser crawler. */
    beforeConsent?: number;
  };
  byCategory: Array<{ purposeId: string | null; purpose: string; cookies: number }>;
  sites: Array<{ host: string; cookies: number; lastScan: string | null }>;
  lastScan: { id: string; at: string; newFindings: number } | null;
  schedules: { active: number; nextRunAt: string | null };
  openAlerts: number;
  choices: Array<{ purposeId: string; purpose: string; allowed: number; refused: number; withdrawn: number }>;
  trend: Array<{ day: string; allowed: number; refused: number }>;
  policy: { version: number; publishedAt: string } | null;
}

export type View = 'overview' | 'cookies' | 'scans' | 'policy' | 'banner' | 'complaint';

/** One region rule on a cookie banner: regions as entered, the model they get. */
export interface GeoRule { regions: string[]; model: 'opt_in' | 'notice_only' | 'opt_out' }

/**
 * A cookie a browser crawl saw before consent is a finding unless a person
 * confirmed it as strictly necessary, or under a purpose that does not rest on
 * consent. Same rule as the API's dashboard figure and compliance check.
 */
export function setBeforeConsent(c: CookieRow, purposes: PurposeRow[]): boolean {
  if (!c.setBeforeConsent) return false;
  if (c.status !== 'confirmed') return true;
  if (!c.purposeId) return false;
  const p = purposes.find((x) => x.id === c.purposeId);
  return !p || p.current.basis === 'consent';
}

export const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

/**
 * How long a cookie lasts, in the words the published policy uses.
 * A hand-set 0 is how the console records a session cookie.
 */
export function expires(c: Pick<CookieRow, 'expiryDays' | 'expirySource'>): string {
  if (c.expirySource === 'session') return 'Session';
  const d = c.expiryDays;
  if (d === null || d === undefined) return 'Not known';
  if (d === 0) return 'Session';
  if (d % 365 === 0) return plural(d / 365, 'year');
  if (d >= 28 && d % 30 === 0) return plural(d / 30, 'month');
  return plural(d, 'day');
}

export function every(days: number): string {
  if (days === 1) return 'Daily';
  if (days === 7) return 'Weekly';
  return plural(days, 'day');
}

export function pct(part: number, whole: number): string {
  if (!whole) return '—';
  return `${Math.round((part / whole) * 1000) / 10}%`;
}
